import React, { useEffect, useState } from 'react';
import SectionedDialog from './SectionedDialog';
import AccessSection from './AccessSection';
import PropertiesSection from './PropertiesSection';

// admin-node-station-edit slug: FAIL-CLOSED allowlist for node_prop editing.
// node_prop carries the node's credentials (apikey/token) and handshake/auth/
// control-plane wiring, so the Properties section is read-only by DEFAULT —
// only these explicitly-allowlisted categories are operator-editable, and any
// category NOT listed (incl. a newly introduced one) stays read-only. This is
// the client half of the guard; the BFF (aveq-admin/node_props_guard.py) and the
// hub (aveq-hub/node_props.py) REJECT any non-allowlisted write — those are the
// security boundary, this list is a UX courtesy. The three lists live
// independently (no shared module spans JS/BFF/hub) and are each pinned by a
// test — keep them in sync.
// app-helper-unification D3: imported_networks + referenced_helpers editable
// post-provision (cross-node discovery, not credentials) — mirror of the hub
// + BFF allowlists. Rendered as CSV text fields by PropertiesSection.
// app-packaging Phase 3: apps_included = CSV of app ids the node hosts (/repo/apps),
// the desired-app set the worker's app-reconcile converges toward.
export const EDITABLE_NODE_PROP_KEYS = ['accepted_providers', 'imported_networks', 'referenced_helpers', 'apps_included', 'location'];
// Credential categories: shown masked (••••••••), read-only, never patched. The
// raw value never reaches the browser's DOM/state via PropertiesSection.
export const MASKED_NODE_PROP_KEYS = ['apikey', 'token'];

/**
 * NodeEditModal — the unified Node editor (admin-node-station-edit slug).
 * Replaces the flat "Info" modal with a SectionedDialog (same chrome as Add
 * Node), folding the former separate "Manage Access" modal in as a section.
 *
 * Sections:
 *   - Name        — inline rename (PATCH /api/nodes/{id}).
 *   - Access      — protect/unprotect (AccessSection; the old ProtectModal).
 *   - Helpers     — read-only: LOADED helpers (probe) + origin, AND the
 *                   running-vs-latest helper versions the node last reported
 *                   (worker-helper-actual-state-report drift + liveness).
 *   - Properties  — raw node_prop view/edit (critical categories read-only),
 *                   GET/PATCH /api/nodes/{id}/properties.
 *
 * admin-node-edit-save: Name + Properties defer to the dialog's SINGLE primary
 * Save (same approach as Edit Station) — editing accumulates in local state and
 * one Save commits the rename and/or the node_prop patch, then closes. Access
 * stays self-acting (protect/unprotect is an immediate toggle, as in Edit
 * Station). Save + Cancel are the only ways to commit / exit.
 */
export default function NodeEditModal({
  nodeId, detail, networkCanonical, onClose, onChanged,
}) {
  const [activeCat, setActiveCat] = useState('name');
  const [nameDraft, setNameDraft] = useState(detail?.name || '');
  const [helpers, setHelpers] = useState(null);    // {loading}|{loaded,available}|{error}
  // worker-helper-actual-state-report: the node's last-reported running-vs-latest
  // helper drift ({rows, reported_at, desired, latest_known}), loaded in lockstep
  // with the probe when the Helpers section opens.
  const [helperState, setHelperState] = useState(null);
  // Properties section (admin-node-station-edit): lazy-loaded node_prop map +
  // the accumulated edit patch (from PropertiesSection).
  const [nodeProps, setNodeProps] = useState(null);   // {loading}|{props}|{error}
  const [propsPatch, setPropsPatch] = useState({});
  // The node's `location` ("lat,lng"), edited in its own section; null = unchanged.
  const [locationDraft, setLocationDraft] = useState(null);
  // admin-node-edit-save: one deferred Save commits BOTH the rename and the
  // node_prop patch (like Edit Station). No per-section save buttons — Save +
  // Cancel are the only ways to commit / exit.
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // admin-node-edit-save: the single primary action (SectionedDialog's Save).
  // Commits the rename and/or the accumulated node_prop patch (whichever changed)
  // in one go, then closes — mirrors Edit Station. On error it stays open with the
  // message in the footer; nothing changed → just closes.
  const submit = async () => {
    const name = nameDraft.trim();
    const nameChanged = !!name && name !== detail?.name;
    const patch = { ...propsPatch };
    if (locationDraft !== null && locationDraft.trim() !== ((nodeProps && nodeProps.props
        && nodeProps.props.location) || '')) {
      patch.location = locationDraft.trim() || null;
    }
    const propsChanged = Object.keys(patch).length > 0;
    if (!nameChanged && !propsChanged) { onClose(); return; }
    setSubmitting(true); setError(null);
    try {
      if (nameChanged) {
        const r = await fetch(`/api/nodes/${nodeId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        if (!r.ok) { const t = await r.text(); throw new Error(t || `HTTP ${r.status}`); }
      }
      if (propsChanged) {
        const r = await fetch(`/api/nodes/${nodeId}/properties`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ props: patch }),
        });
        if (!r.ok) { const t = await r.text(); throw new Error(t || `HTTP ${r.status}`); }
      }
      if (onChanged) onChanged();
      onClose();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  // Lazy-load loaded helpers the first time the Helpers section is opened.
  useEffect(() => {
    if (activeCat !== 'helpers' || (helpers && !helpers.loading)) return;
    let cancelled = false;
    (async () => {
      setHelpers({ loading: true });
      try {
        const q = networkCanonical ? `?network=${encodeURIComponent(networkCanonical)}` : '';
        const [hr, pr, sr] = await Promise.all([
          fetch(`/api/nodes/${nodeId}/loaded-helpers${q}`),
          fetch('/api/providers'),
          fetch(`/api/nodes/${nodeId}/helper-state`),
        ]);
        const hbody = await hr.json();
        const pbody = await pr.json().catch(() => ({}));
        const sbody = await sr.json().catch(() => null);   // reported drift; null on error
        const catalog = {};
        ((pbody && pbody.providers) || []).forEach((p) => { if (p.module) catalog[p.module] = p; });
        const loaded = (hbody.loaded || []).map((name) => {
          const cat = catalog[name];
          return {
            name,
            origin: cat ? 'provider' : 'local',
            providerName: cat ? (cat.display_name || cat.module || 'provider') : '',
          };
        });
        if (!cancelled) { setHelpers({ loaded, available: !!hbody.available }); setHelperState(sbody); }
      } catch (e) {
        if (!cancelled) setHelpers({ error: String(e.message || e) });
      }
    })();
    return () => { cancelled = true; };
    // `helpers` is read for the load-once guard but MUST NOT be a dep: setting
    // {loading} would re-run the effect, whose cleanup cancels the in-flight
    // fetch → stuck at "Loading…". (admin-node-station-edit hang fix.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat, nodeId, networkCanonical]);

  // Lazy-load node_prop the first time the Properties section is opened.
  useEffect(() => {
    if (!['properties', 'location'].includes(activeCat) || (nodeProps && !nodeProps.loading)) return;
    let cancelled = false;
    (async () => {
      setNodeProps({ loading: true });
      try {
        const r = await fetch(`/api/nodes/${nodeId}/properties`);
        if (!r.ok) { const t = await r.text(); throw new Error(t || `HTTP ${r.status}`); }
        const body = await r.json();
        // hub wraps as {entity:{props}}; BFF passes that through.
        const props = (body && body.entity && body.entity.props) || (body && body.props) || {};
        if (!cancelled) setNodeProps({ props });
      } catch (e) {
        if (!cancelled) setNodeProps({ error: String(e.message || e) });
      }
    })();
    return () => { cancelled = true; };
    // `nodeProps` read for the load-once guard but MUST NOT be a dep (see the
    // helpers effect above — same self-cancel hang).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat, nodeId]);

  const renderProperties = () => {
    if (!nodeProps || nodeProps.loading) return <div style={{ color: '#888', fontSize: '0.82rem' }}>Loading…</div>;
    if (nodeProps.error) return <div style={{ color: '#c00', fontSize: '0.82rem' }}>{nodeProps.error}</div>;
    // admin-node-edit-save: edits accumulate into propsPatch; the dialog's single
    // Save commits them (no per-section save button anymore).
    return (
      <PropertiesSection
        properties={nodeProps.props}
        ownedKeys={['location']}
        editableKeys={EDITABLE_NODE_PROP_KEYS}
        maskedKeys={MASKED_NODE_PROP_KEYS}
        onPatchChange={setPropsPatch} />
    );
  };

  const renderLocation = () => {
    if (!nodeProps || nodeProps.loading) return <div style={mutedStyle}>Loading…</div>;
    if (nodeProps.error) return <div style={{ color: '#c00', fontSize: '0.82rem' }}>{nodeProps.error}</div>;
    const value = locationDraft !== null ? locationDraft : (nodeProps.props.location || '');
    return (
      <div>
        <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 4 }}>latitude, longitude</div>
        <input aria-label="node location" value={value} placeholder="41.0956, -73.4175"
               onChange={(e) => { setLocationDraft(e.target.value); setError(null); }}
               style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
        <div style={{ ...mutedStyle, marginTop: 8 }}>
          Where this device is. A sensor with no location of its own is placed here, or at its
          parent node's location. Leave empty to clear.
        </div>
      </div>
    );
  };

  const renderName = () => (
    <div>
      <div style={{ fontSize: '0.85rem', marginBottom: 14 }}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: '#888' }}>nodeId </span>
          <code style={{ fontSize: '0.78rem' }}>{detail?.nodeId}</code>
        </div>
        {detail?.compose_target && (
          <div>in-stack container <code style={{ fontSize: '0.78rem' }}>{detail.compose_target}</code> — admin-managed</div>
        )}
      </div>
      <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 4 }}>node name</div>
      <input value={nameDraft} onChange={(e) => { setNameDraft(e.target.value); setError(null); }}
             onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
             maxLength={64} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
    </div>
  );

  // The helpers currently LOADED on the node (from probe).
  const renderLoadedHelpers = () => {
    if (!helpers || helpers.loading) return <div style={mutedStyle}>Loading…</div>;
    // admin-node-edit-save: a node that can't be probed reads the same either way —
    // the BFF returns {available:false} for an offline/unreachable/timed-out probe,
    // and a transport error lands in `helpers.error`. Both mean "can't query the
    // node", so show ONE clear message (raw error on hover) instead of a raw red
    // error string / the old "not reachable" wording.
    if (helpers.error || !helpers.available) {
      return <div style={mutedStyle} title={helpers.error || 'probe returned unavailable'}>Node offline. Unable to query</div>;
    }
    if (!helpers.loaded.length) return <div style={mutedStyle}>No helpers loaded (bare relay).</div>;
    return (
      <div style={helperGridStyle}>
        {helpers.loaded.map((h) => (
          <React.Fragment key={h.name}>
            <code style={{ fontSize: '0.78rem' }}>{h.name}</code>
            {h.origin === 'local'
              ? <span style={localTextStyle} title="node-local helper">local</span>
              : <span style={providerColStyle} title={`provider: ${h.providerName}`}>{h.providerName}</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // worker-helper-actual-state-report: what the node last REPORTED it is running,
  // vs the desired set + latest catalog version (running-vs-latest drift +
  // last-reported liveness). Read-only. Loads in lockstep with the probe above.
  const renderReportedState = () => {
    if (!helpers || helpers.loading) return null;          // still loading with the probe
    const s = helperState || {};
    const rows = s.rows || [];
    const reported = s.reported_at ? new Date(s.reported_at) : null;
    const stale = reported && (Date.now() - reported.getTime()) > 1000 * 60 * 60; // >1h
    return (
      <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 12 }}>
        <div style={sectionLabelStyle}>Reported state (running vs latest)</div>
        {!reported && (
          <div style={mutedStyle}>
            This node hasn’t reported its running helper set. In-stack nodes don’t report;
            off-stack workers (docker / bare-metal) report after each reconcile.
          </div>
        )}
        {reported && (
          <>
            <div style={{ ...mutedStyle, marginBottom: 6 }}>
              reported {fmtAgo(reported)}
              {stale && <span style={{ color: '#c0392b' }}> · ⚠ stale</span>}
              {s.latest_known === false &&
                <span title="bundles index.json not available on this host"> · latest unknown</span>}
            </div>
            {rows.length === 0 && <div style={mutedStyle}>No helpers reported.</div>}
            <div style={reportGridStyle}>
              {rows.map((row) => (
                <React.Fragment key={row.module}>
                  <code style={{ fontSize: '0.78rem' }}>{row.module}</code>
                  <span style={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                    <span style={{ fontFamily: 'monospace' }}>{row.running || '—'}</span>
                    {row.latest && row.latest !== row.running &&
                      <span style={{ color: '#888' }}> → {row.latest}</span>}
                  </span>
                  <span style={{ ...(reportStatusStyle[row.status] || {}), fontSize: '0.74rem' }}>{row.status}</span>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderHelpers = () => (
    <div>
      {renderLoadedHelpers()}
      {renderReportedState()}
    </div>
  );

  const categories = [
    { id: 'name', label: 'Name', description: 'Node identity + rename.', render: renderName },
    { id: 'access', label: 'Access', description: 'Protect / unprotect chat-user access.',
      render: () => <AccessSection level="node" id={nodeId} name={detail?.name} onChanged={onChanged} /> },
    { id: 'helpers', label: 'Helpers',
      description: 'Loaded helpers + the running-vs-latest versions the node last reported (read-only).',
      render: renderHelpers },
    { id: 'location', label: 'Location', description: 'Where this device physically is.',
      render: renderLocation },
    { id: 'properties', label: 'Properties', description: 'Raw node properties. Critical (handshake/auth) keys are read-only.',
      render: renderProperties },
  ];

  return (
    <SectionedDialog
      title={<code style={titleNameStyle}>{detail?.name || '(unnamed node)'}</code>}
      titleText={detail?.name || 'node'}
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel="Save"
      submittingLabel="Saving…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      width={680}
    />
  );
}

// Title name styled like Add Node's "Add child of <name>" — a muted monospace
// <code> at 0.85em — so Node + Station Edit read the name the same way.
const titleNameStyle = {
  fontSize: '0.85em', color: '#555',
};
const inputStyle = {
  flex: 1, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.85rem',
};
const helperGridStyle = {
  display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4, alignItems: 'baseline',
};
const providerColStyle = { fontSize: '0.74rem', color: '#555' };
const localTextStyle = { fontSize: '0.74rem', color: '#999', fontStyle: 'italic' };
// worker-helper-actual-state-report: reported running-vs-latest drift styles.
const mutedStyle = { color: '#888', fontSize: '0.82rem' };
const sectionLabelStyle = {
  fontSize: '0.7rem', color: '#888', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.4px',
};
const reportGridStyle = {
  display: 'grid', gridTemplateColumns: 'auto 1fr auto', columnGap: 12, rowGap: 4,
  alignItems: 'baseline',
};
const reportStatusStyle = {
  current: { color: '#2e8b57' },
  behind:  { color: '#c0392b', fontWeight: 600 },
  missing: { color: '#c0392b', fontWeight: 600 },
  extra:   { color: '#b8860b' },
};

// Compact "time ago" for the last-reported line (no external dep).
function fmtAgo(d) {
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}
